import React, { useState } from 'react'
import { ImportImage, Input, InputCheck, SelectAnimalFetchInput, SelectInput } from 'src/components'
import { useFormContext } from 'react-hook-form'
import { BloodGradeDto, IAnimal } from 'src/dto/animal.dto'
import { BreedDto } from 'src/dto/breed.dto'
import { LotDto } from 'src/dto/lot.dto'
import { PastureDto } from 'src/dto/pasture.dto'
import { CoatDto } from 'src/dto/coat.dto'
import { useStyles } from './styles'
import { cleanWeightValue, formatDateToInputValue, formatWeight, getNumericValue } from 'src/utils'
import { Grid } from '@material-ui/core'
import { TextArea } from 'src/components/TextArea'
import { parseWeight } from '../../utils/functions'
import { AnimalRegistrationForm } from '../..'

interface AnimalCompleteRegistrationProps {
  breedList: BreedDto[]
  lotsList: LotDto[]
  pastureList: PastureDto[]
  coatList: CoatDto[]
  bloodGradeList: BloodGradeDto[]
  setAnimalImage: (image: File | null) => void
  handleSaveImage: (animalId: string) => Promise<void>
  isDiagnosticRestiger?: boolean
}

export const AnimalCompleteRegistration: React.FC<AnimalCompleteRegistrationProps> = ({
  breedList,
  lotsList,
  pastureList,
  coatList,
  bloodGradeList,
  setAnimalImage,
  handleSaveImage,
  isDiagnosticRestiger = false
}) => {
  const {
    register,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors }
  } = useFormContext<AnimalRegistrationForm>()

  const formData = getValues()
  const [displayWeight, setDisplayWeight] = useState<string>(() => parseWeight(formData?.pesoAtual!))
  const [multiCow, setMultiCow] = useState<boolean>(false)

  const styles = useStyles()

  return (
    <>
      <div className={styles.checkInputContainer}>
        <InputCheck
          id='animal-comprado'
          value='animal_comprado'
          label='Animal comprado'
          name='animal-comprado'
          checked={watch('tipoAnimal') === 'animal_comprado'}
          error={errors?.tipoAnimal?.message}
          disabled={isDiagnosticRestiger}
          register={register}
          onChange={(e) => {
            const value = e.target.value
            setValue('tipoAnimal', value)
            clearErrors('tipoAnimal')
          }}
        />
        <InputCheck
          id='nasceu-fazenda'
          value='nasceu_fazenda'
          label='Animal nasceu na fazenda'
          checked={watch('tipoAnimal') === 'nasceu_fazenda'}
          name='nasceu-fazenda'
          register={register}
          disabled={isDiagnosticRestiger}
          onChange={(e) => {
            const value = e.target.value
            setValue('tipoAnimal', value)
            clearErrors('tipoAnimal')
          }}
          className={errors?.tipoAnimal?.message ? 'input-check--error' : ''}
        />
        <InputCheck
          id='animal-externo'
          value='animal_externo'
          label='Animal externo'
          checked={watch('tipoAnimal') === 'animal_externo'}
          name='animal-externo'
          register={register}
          disabled={isDiagnosticRestiger}
          onChange={(e) => {
            const value = e.target.value
            setValue('tipoAnimal', value)
            clearErrors('tipoAnimal')
          }}
          className={errors?.tipoAnimal?.message ? 'input-check--error' : ''}
        />
      </div>

      <section className={styles.section}>
        <legend className={styles.sectionTitle}>Dados do animal</legend>
        <div className={styles.checkInputContainer}>
          <InputCheck
            id='sexo-male'
            value='M'
            label='Sexo macho'
            name='sexo_m'
            register={register}
            error={errors?.sexo?.message}
            checked={watch('sexo') === 'M'}
            onChange={(e) => {
              const value = e.target.value
              setValue('sexo', value)
              clearErrors('sexo')
            }}
          />

          <InputCheck
            id='sexo-female'
            value='F'
            register={register}
            label='Sexo fêmea'
            name='sexo_f'
            checked={watch('sexo') === 'F'}
            onChange={(e) => {
              const value = e.target.value
              setValue('sexo', value)
              clearErrors('sexo')
            }}
            className={errors?.sexo?.message ? 'input-check--error' : ''}
          />

          <InputCheck
            id='touros-multiplos'
            value='tourosMultiplos'
            label='Touros múltiplos'
            name='tourosMultiplos'
            register={register}
            type={'checkbox'}
            disabled={isDiagnosticRestiger}
            checked={multiCow}
            onChange={(e) => {
              const value = e.target.checked
              setMultiCow(value)
            }}
            className={errors?.tourosMultiplos?.message ? 'input-check--error' : ''}
          />
        </div>
        <div className={styles.inputContainer}>
          <Input
            id='nome'
            label='Nome / Apelido'
            error={errors?.nome?.message}
            placeholder='Qual o nome ou apelido do animal?'
            value={watch('nome') || ''}
            onChange={(value: string) => {
              setValue('nome', value)
              clearErrors('nome')
            }}
          />

          {watch('tipoAnimal') === 'animal_comprado' && (
            <Input
              label='Fazenda da compra'
              value={watch('fazendaOrigem') || ''}
              error={errors?.fazendaOrigem?.message}
              placeholder='De qual fazenda foi comprado o animal?'
              onChange={(value: string) => {
                setValue('fazendaOrigem', value)
                clearErrors('fazendaOrigem')
              }}
            />
          )}
          <div className={styles.container}>
            <Input
              id='data-nascimento'
              type='date'
              max='2999-12-31'
              label='Data de nascimento'
              error={errors?.dataNascimento?.message}
              disabled={isDiagnosticRestiger}
              onChange={(value: string) => {
                const newDate = new Date(value).toISOString()
                setValue('dataNascimento', newDate)
                clearErrors('dataNascimento')
              }}
              value={formatDateToInputValue(watch('dataNascimento') || '')}
            />
            <Input
              label='Peso'
              id='pesoAtual'
              className='input_text'
              value={displayWeight}
              error={errors?.pesoAtual?.message}
              placeholder='Quantos KG o animal tem?'
              onChange={(value: string) => {
                const cleanValue = cleanWeightValue(value)

                const formatted = formatWeight(cleanValue)
                setDisplayWeight(formatted)

                const numericValue = getNumericValue(formatted)
                setValue('pesoAtual', numericValue)
                clearErrors('pesoAtual')
              }}
            />

            <Input
              label='Número'
              value={watch('cdn') || ''}
              error={errors?.cdn?.message}
              placeholder='Número'
              onChange={(value: string) => {
                setValue('cdn', value)
                clearErrors('cdn')
              }}
            />
          </div>
          <div>
            <Grid container spacing={0} className={styles.gridContainerThreeColumns}>
              <Grid item xs={12} sm={4}>
                <Input
                  label='Brinco'
                  value={watch('brinco') || ''}
                  error={errors?.brinco?.message}
                  placeholder='Brinco'
                  onChange={(value: string) => {
                    setValue('brinco', value)
                    clearErrors('brinco')
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Input
                  label='RGN'
                  value={watch('rgn') || ''}
                  error={errors?.rgn?.message}
                  placeholder='RGN'
                  onChange={(value: string) => {
                    setValue('rgn', value)
                    clearErrors('rgn')
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Input
                  label='RGD'
                  value={watch('rgd') || ''}
                  error={errors?.rgd?.message}
                  placeholder='RGD'
                  onChange={(value: string) => {
                    const formatedValue = value.replace(/\D/g, '')
                    setValue('rgd', formatedValue)
                    clearErrors('rgd')
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <SelectInput
                  label='Raça'
                  selectBy='idRaca'
                  showBy={['descricao']}
                  options={breedList || []}
                  value={watch('raca') || ''}
                  placeholder='Selecione uma raça'
                  onChange={(value: any) => {
                    setValue('raca', value.idRaca)
                    clearErrors('raca')
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <SelectInput
                  label='Grau'
                  selectBy='idSangue'
                  showBy={['descricao']}
                  options={bloodGradeList || []}
                  error={errors?.grau?.message}
                  value={watch('grau') || ''}
                  placeholder='Selecione um grau'
                  onChange={(value: any) => {
                    setValue('grau', value.idSangue)
                    clearErrors('grau')
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <SelectInput
                  label='Pelagem'
                  selectBy='pelagem'
                  showBy={['descricao']}
                  options={coatList || []}
                  error={errors?.pelagem?.message}
                  value={watch('pelagem') || ''}
                  placeholder='Selecione uma pelagem'
                  onChange={(value: any) => {
                    setValue('pelagem', value.pelagem)
                    clearErrors('pelagem')
                  }}
                />
              </Grid>
            </Grid>
          </div>
          <div style={{ marginTop: 16 }} className={styles.container}>
            <SelectInput
              label='Pasto'
              selectBy='id'
              showBy={['descricao']}
              options={pastureList || []}
              value={watch('idPastoAtual') || ''}
              error={errors?.idPastoAtual?.message}
              placeholder='Em qual pasto o animal está?'
              onChange={(value: any) => {
                setValue('idPastoAtual', value.id)
                clearErrors('idPastoAtual')
              }}
            />
            <SelectInput
              label='Lote'
              selectBy='id'
              showBy={['descricao']}
              options={lotsList || []}
              value={watch('idLoteAtual') || ''}
              error={errors?.idLoteAtual?.message}
              placeholder='Qual o lote do animal?'
              onChange={(value: any) => {
                setValue('idLoteAtual', value.id)
                clearErrors('idLoteAtual')
              }}
            />
          </div>
          {multiCow && (
            <div className={styles.inputContainer}>
              <SelectAnimalFetchInput
                label='Adicionar touros múltiplos'
                selectBy='idAnimal'
                showBy={['cdn', 'nome']}
                filteredSex='Macho'
                value={''}
                notShowAnimals={Array.isArray(watch('tourosMultiplos')) ? watch('tourosMultiplos') : []}
                error={errors?.tourosMultiplos?.message}
                placeholder='Selecione um touro'
                onChange={(animal: IAnimal) => {
                  const atual = Array.isArray(watch('tourosMultiplos')) ? watch('tourosMultiplos') : []
                  setValue('tourosMultiplos', [...atual, animal])
                }}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                {watch('tourosMultiplos').map((animal: IAnimal) => (
                  <div
                    key={animal.idAnimal}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#fff',
                      borderRadius: 16,
                      padding: '4px 12px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.875rem',
                        marginRight: 8,
                        color: '#784A42'
                      }}
                    >{`${animal.brinco || ''} - ${animal.nome || ''}`}</span>
                    <button
                      type='button'
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#784A42',
                        fontWeight: 'bold'
                      }}
                      onClick={() => {
                        setValue(
                          'tourosMultiplos',
                          watch('tourosMultiplos')?.filter((a: any) => a.idAnimal !== animal.idAnimal)
                        )
                      }}
                      aria-label='Remover'
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <section className={styles.section}>
        <legend className={styles.sectionTitle}>Geneologia</legend>
        <div>
          <Grid container spacing={0} className={styles.gridContainerTwoColumns}>
            <Grid item xs={12} sm={6}>
              <SelectAnimalFetchInput
                label='Pai do animal'
                selectBy='idAnimal'
                showBy={['cdn', 'nome']}
                filteredSex='Macho'
                value={watch('idAnimalPai') || ''}
                error={errors?.idAnimalPai?.message}
                placeholder='Número de registro do pai do animal'
                disabled={isDiagnosticRestiger}
                onChange={(value: IAnimal) => {
                  setValue('idAnimalPai', value.idAnimal!)
                  setValue('animalPai', value)
                  clearErrors('idAnimalPai')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SelectAnimalFetchInput
                label='Mãe do animal'
                selectBy='idAnimal'
                showBy={['cdn', 'nome']}
                filteredSex='Fêmea'
                value={watch('idAnimalMae') || ''}
                error={errors?.idAnimalMae?.message}
                placeholder='Número de registro da mãe do animal'
                disabled={isDiagnosticRestiger}
                onChange={(value: IAnimal) => {
                  setValue('idAnimalMae', value.idAnimal!)
                  setValue('animalMae', value)
                  clearErrors('idAnimalMae')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                style={{ backgroundColor: 'rgba(212, 199, 188, 0.3)' }}
                disabled
                label='Raça do pai'
                value={breedList.find((b) => b.idRaca === watch('animalPai')?.raca)?.descricao || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                disabled
                style={{ backgroundColor: 'rgba(212, 199, 188, 0.3)' }}
                label='Raça da mãe'
                value={breedList.find((b) => b.idRaca === watch('animalMae')?.raca)?.descricao || ''}
              />
            </Grid>
          </Grid>
        </div>
      </section>
      <section className={styles.section}>
        <legend className={styles.sectionTitle}>Detalhes</legend>
        <div>
          <TextArea
            label='Observações'
            id='observacoes'
            value={watch('observacoes') || ''}
            error={errors?.observacoes?.message}
            placeholder='Coloque suas observações aqui.'
            onChange={(value: string) => {
              setValue('observacoes', value)
            }}
          />

          <ImportImage
            onChange={async (image) => {
              setAnimalImage(image)
              const animalId = watch('idAnimal')
              if (animalId) {
                await handleSaveImage(animalId)
              }
            }}
          />
        </div>
      </section>
    </>
  )
}
